import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { RECOMMEND_TAG_TYPE } from '../../../../constants/service/app/tagConstants';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_TAG_SAVE_MODEL,
  RECOMMEND_TAG_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_TAG_LIST_SEARCH_OPTION_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 태그 리스트
   */
  recommendTagList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_TAG_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_TAG_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 태그 상세
   */
  recommendTagDetail: {
    /**
     *  추천 태그 정보
     */
    recommendTagInfo: {
      /**
       *  추천 태그  식별 id
       */
      recommendTagId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 태그 타입
       */
      type: RECOMMEND_TAG_TYPE.SEARCH_RECOMMEND,
      /**
       *  추천 태그 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 태그 아이템 리스트
     */
    recommendTagItemList: [],
  },

  /**
   *  추천 태그 등록/복사
   */
  recommendTagSave: RECOMMEND_TAG_SAVE_MODEL,
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 상품 후기 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 태그 리스트 가져오기 성공
     */
    [actions.GET_TAG_REVIEW_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendTagList = {
          ...draft.recommendTagList,
          ...action.payload.recommendTagList,
        };
      });
    },

    /**
     *  추천 태그 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_TAG_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendTagDetail = action.payload.recommendTagDetail;
      });
    },

    /**
     *  추천 태그 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_TAG_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 태그 리스트
         */
        draft.recommendTagSave.recommendTagItemList.itemList = {
          ...draft.recommendTagSave.recommendTagItemList.itemList,
          ...action.payload.recommendTagSave.recommendTagItemList,
        };

        /**
         *  추천 태그 정보
         */
        draft.recommendTagSave.recommendTagInfo = action.payload.recommendTagSave.recommendTagInfo;

        /**
         *  선택된 태그 리스트
         */
        draft.recommendTagSave.recommendTagItemList.selectedItemList = {
          list: action.payload.recommendTagSave.selectedRecommendTagItemList,
          idList: action.payload.recommendTagSave.selectedRecommendTagItemList.map((item) => item.id),
        };

        /**
         *  선택할 태그 아이템 중에 이미 설정된 태그 아이템인 경우에는
         *  상품 태그 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendTagSave.recommendTagItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendTagSave.recommendTagItemList.itemList.list,
          selectedIdList: draft.recommendTagSave.recommendTagItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  추천 태그 등록하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_CREATE_RECOMMEND_TAG_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  태그 리스트
         */
        draft.recommendTagSave.recommendTagItemList.itemList = action.payload.recommendTagSave.recommendTagItemList;
      });
    },

    /**
     *  추천 태그에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_TAG_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 태그 리스트
         */
        draft.recommendTagSave.recommendTagItemList.itemList = {
          ...draft.recommendTagSave.recommendTagItemList.itemList,
          ...action.payload.recommendTagItemList,
        };

        /**
         *  선택할 태그 아이템 중에 이미 설정된 태그 아이템인 경우에는
         *  태그 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendTagSave.recommendTagItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendTagSave.recommendTagItemList.itemList.list,
          selectedIdList: draft.recommendTagSave.recommendTagItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  태그 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_TAG_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 태그 리스트
         */
        draft.recommendTagSave.recommendTagItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  선택된 태그 아이템의 중복 제거하기
         */
        draft.recommendTagSave.recommendTagItemList.selectedItemList.list = uniqBy(
          draft.recommendTagSave.recommendTagItemList.selectedItemList.list,
          'id',
        );

        draft.recommendTagSave.recommendTagItemList.selectedItemList.idList =
          draft.recommendTagSave.recommendTagItemList.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 태그 아이템 중에 이미 설정된 태그 아이템인 경우에는
         *  태그 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendTagSave.recommendTagItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendTagSave.recommendTagItemList.itemList.list,
          selectedIdList: draft.recommendTagSave.recommendTagItemList.selectedItemList.idList,
        });
      });
    },
  },
  initialState,
);
